// src/utils/passwordValidator.ts
// Centralized password validation for web application
// Ensures consistent password requirements across registration, reset, and change password

export interface PasswordRequirement {
  label: string;
  test: (password: string) => boolean;
  met?: boolean;
}

export interface PasswordValidationResult {
  isValid: boolean;
  requirements: PasswordRequirement[];
  strength: 'weak' | 'medium' | 'strong';
  score: number;
}

/**
 * Password requirements
 * Must match backend validation in backend/src/utils/passwordValidator.js
 */
export const PASSWORD_REQUIREMENTS: PasswordRequirement[] = [
  {
    label: 'At least 8 characters',
    test: (password: string) => password.length >= 8,
  },
  {
    label: 'Contains uppercase letter',
    test: (password: string) => /[A-Z]/.test(password),
  },
  {
    label: 'Contains lowercase letter',
    test: (password: string) => /[a-z]/.test(password),
  },
  {
    label: 'Contains a number',
    test: (password: string) => /\d/.test(password),
  },
  {
    label: 'Contains special character (!@#$%^&*)',
    test: (password: string) => /[!@#$%^&*(),.?":{}|<>]/.test(password),
  },
];

/**
 * Validate password against all requirements
 * @param password - Password to validate
 * @returns Validation result with met requirements and strength
 */
export const validatePassword = (password: string): PasswordValidationResult => {
  if (!password) {
    return {
      isValid: false,
      requirements: PASSWORD_REQUIREMENTS.map(req => ({ ...req, met: false })),
      strength: 'weak',
      score: 0,
    };
  }

  // Check each requirement
  const requirements = PASSWORD_REQUIREMENTS.map(req => ({
    ...req,
    met: req.test(password),
  }));

  // Calculate how many requirements are met
  const metCount = requirements.filter(req => req.met).length;
  const totalRequirements = requirements.length;
  const score = (metCount / totalRequirements) * 100;

  // All requirements must be met
  const isValid = metCount === totalRequirements;

  // Determine strength
  let strength: 'weak' | 'medium' | 'strong';
  if (score < 60) {
    strength = 'weak';
  } else if (score < 100) {
    strength = 'medium';
  } else {
    strength = 'strong';
  }

  return {
    isValid,
    requirements,
    strength,
    score,
  };
};

/**
 * Quick validation - returns true if password meets all requirements
 * @param password - Password to validate
 * @returns True if valid, false otherwise
 */
export const isPasswordValid = (password: string): boolean => {
  return validatePassword(password).isValid;
};

/**
 * Get password strength indicator
 * @param password - Password to check
 * @returns Strength level
 */
export const getPasswordStrength = (password: string): 'weak' | 'medium' | 'strong' => {
  return validatePassword(password).strength;
};

/**
 * Get human-readable error message for invalid password
 * @param password - Password to validate
 * @returns Error message or null if valid
 */
export const getPasswordErrorMessage = (password: string): string | null => {
  const result = validatePassword(password);
  
  if (result.isValid) {
    return null;
  }

  const unmetRequirements = result.requirements
    .filter(req => !req.met)
    .map(req => req.label);

  if (unmetRequirements.length === 0) {
    return null;
  }

  if (unmetRequirements.length === 1) {
    return `Password must have: ${unmetRequirements[0]}`;
  }

  return `Password must have: ${unmetRequirements.join(', ')}`;
};

/**
 * Validate password match (for confirmation fields)
 * @param password - Original password
 * @param confirmPassword - Confirmation password
 * @returns True if passwords match and are valid
 */
export const validatePasswordMatch = (
  password: string,
  confirmPassword: string
): { isValid: boolean; error?: string } => {
  if (!password || !confirmPassword) {
    return { isValid: false, error: 'Please enter both passwords' };
  }

  if (password !== confirmPassword) {
    return { isValid: false, error: 'Passwords do not match' };
  }

  const validation = validatePassword(password);
  if (!validation.isValid) {
    return { isValid: false, error: getPasswordErrorMessage(password) || 'Invalid password' };
  }

  return { isValid: true };
};

const passwordValidator = {
  validatePassword,
  isPasswordValid,
  getPasswordStrength,
  getPasswordErrorMessage,
  validatePasswordMatch,
  PASSWORD_REQUIREMENTS,
};

export default passwordValidator;

